<template>
  <validation-observer ref="validationobserver" v-slot="{}">
    <b-form @submit.prevent="salvarPrestacao">
      <b-overlay :show="screenBlocked">
        <b-card header-tag="header" class="m-3" no-body>
          <template #header>
            <h6 class="mb-0 text-center">
              <span class="titulo-sagres font-weight-bold">
                Entrega de Relatório da LRF e CI
              </span>
              <btn-voltar />
              <b-button class="float-right m-1" size="sm" type="submit">
                <font-awesome-icon icon="save" />
                Enviar
              </b-button>
            </h6>
          </template>
          <b-card-body>
            <b-row>
              <b-col cols="12" md="3">
                <validation-provider rules="required|min_value:1" v-slot="validationContext" name="ano">
                  <b-form-group id="input-group-ano" label-size="md" label="Exercício (ano)*">
                    <b-form-select id="select-ano" v-model="prestacaoLRFCI.ano" :options="comboAnos"
                      :state="getValidationState(validationContext)">
                    </b-form-select>
                    <b-form-invalid-feedback id="select-ano-live-feedback">
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="9" xl="6">
                <validation-provider rules="required|min_value:1" v-slot="validationContext" name="tipo-relatorio">
                  <b-form-group id="input-group-tipo-relatorio" label-size="md" label="Tipo de Relatório *">
                    <b-form-select id="select-tipo-relatorio" v-model="prestacaoLRFCI.tipo"
                      :options="comboTiposDeRelatorio" @change="updatePeriodos"
                      :state="getValidationState(validationContext)">
                    </b-form-select>
                    <b-form-invalid-feedback id="select-tipo-relatorio-live-feedback">
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="6" xl="3">
                <validation-provider rules="required|min_value:1" v-slot="validationContext" name="periodo-prestacao">
                  <b-form-group id="input-group-periodo-prestacao" label-size="md" label="Período Prestação">
                    <b-overlay :show="loadingPeriodos">
                      <b-form-select id="select-filtros-periodo-prestacao" v-model="prestacaoLRFCI.periodo"
                        :options="comboPeriodos" :disabled="!comboPeriodos.length"
                        :state="getValidationState(validationContext)">
                      </b-form-select>
                      <b-form-invalid-feedback id="select-periodo-prestacao-live-feedback">
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-overlay>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="6" offset-xl="3">
                <validation-provider rules="required" v-slot="validationContext" name="arquivo">
                  <b-form-group id="input-group-arquivo" label-size="md" label="Arquivo texto do relatório (PDF) *">
                    <b-form-file v-model="prestacaoLRFCI.arquivoPDF" placeholder="Nenhum arquivo selecionado"
                      accept="application/pdf" :state="getValidationState(validationContext)"></b-form-file>
                    <b-form-invalid-feedback id="file-arquivo-live-feedback">
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-overlay>
    </b-form>
  </validation-observer>
</template>

<script lang="ts">
import { Vue, Component, Ref } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import BtnVoltar from '@/components/shared/BtnVoltar.vue'

import { ValidationProvider, ValidationObserver } from "vee-validate";
import { store } from "@/store";
import { UtilsTS } from '@/utilsts'

import { SelectValue } from '@/types/select-value';
import lrfService from '@/services/entregas/lrfService';
import { EntregaPrestacaoDeContasLRFCI } from '@/types/entregas/lrfci/entrega-prestacao-contas-lrfci';

const CompProps = Vue.extend({
  name: 'EditarRelatorioLRFCI',
  components: {
    BtnVoltar,
    ValidationProvider,
    ValidationObserver,
  }
})

/**
 * Tipo usado na função padrão de validação do vee validate. Talvez a gente deva criar um vee-validate mixin.
 */
type ValidationStateParams = {
  dirty: boolean,
  validated: boolean,
  valid: boolean | null
}

@Component
export default class EditarRelatorioLRFCI extends mixins(UtilsTS, CompProps) {
  @Ref('validationobserver') validationobserver: InstanceType<typeof ValidationObserver>
  comboAnos: SelectValue[] = []
  comboPeriodos: SelectValue[] = []
  comboTiposDeRelatorio: SelectValue[] = []
  comboSituacoes: SelectValue[] = []
  prestacaoLRFCI = new EntregaPrestacaoDeContasLRFCI()
  loadingPeriodos = false
  screenBlocked = false

  async salvarPrestacao(): Promise<void> {
    try {
      const isValid = await this.validationobserver.validate();
      if (!isValid) {
        console.error(this.validationobserver)
        return
      }
      this.screenBlocked = true
      const response = await lrfService.salvarLRF(this.prestacaoLRFCI);
      if (response && response.status == 200) {
        this.$toast.success(`O relatório de LRF foi entregue com sucesso!`);
        this.$router.push("/entrega/relatorios-lrf-ci");
      } else {
        this.$toast.error(response.data.msg || "Erro ao salvar o relatório de LRF e CI");
      }
    } catch (e: unknown) {
      console.error('Erro ao salvar relatório de LRF e CI', e, this.prestacaoLRFCI)
      if (e instanceof Error) {
        this.$toast.error(e.message)
      } else {
        this.$toast.error("Erro ao salvar relatório de LRF e CI")
      }
    } finally {
      this.screenBlocked = false
    }
  }
  getValidationState({ dirty, validated, valid = null }: ValidationStateParams): boolean | null {
    return dirty || validated ? valid : null;
  }

  async updatePeriodos(novoTipoRelatorio: string): Promise<void> {
    try {
      this.loadingPeriodos = true
      const periodos = await lrfService.getPeriodosRelatorio(novoTipoRelatorio)
      if (periodos && periodos.length > 0) {
        this.comboPeriodos = [
          { text: 'SELECIONE', value: '0', disabled: true },
          ...periodos.map(p => { return { text: p.DePeriodorelatorio, value: p.IdPeriodorelatorio } })
        ]
      } else {
        this.comboPeriodos.slice(0)
        this.prestacaoLRFCI.periodo = 0
      }
    } finally {
      this.loadingPeriodos = false
    }
  }

  async getCombos(): Promise<void> {
    try {
      const combos = await lrfService.getCombosConsultaLRF()
      this.comboAnos = [
        { text: 'SELECIONE', value: '0', disabled: true },
        ...combos.anosEntrega.map(ae => { return { text: String(ae), value: ae } })
      ]
      this.comboTiposDeRelatorio = [
        { text: 'SELECIONE', value: '0', disabled: true },
        ...combos.tiposRelatorio.map(tr => {
          //console.log(tr)
          return { text: tr.DeTiporelatorio, value: tr.IdTiporelatorio }
        })
      ]
    } catch (e: any) {
      console.error('Erro ao obter combos de prazos de vencimento (LRF)', e)
      throw e
    }
  }

  async mounted(): Promise<void> {
    try {
      this.screenBlocked = true
      await this.getCombos()
    } catch (e: any) {
      console.error('Erro ao montar criação de entrega de relatório da LRF e CI', e)
      this.$router.push("/entrega/relatorios-lrf-ci");
      this.$toast.error(e.message || "Erro ao carregar a tela de entrega de relatório da LRF e CI");
    } finally {
      this.screenBlocked = false
    }
  }
};
</script>

<style>

</style>